import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Truck, Search, CheckCircle2, Loader2 } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { toast } from 'sonner';
import { format, differenceInHours, parseISO, isValid } from 'date-fns';
import { de } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import StatusBadge from '@/components/ui/StatusBadge';
import OrderDocuments from '@/components/orders/OrderDocuments';
import { getOrderItems } from '@/lib/orderFields';
import { useAuth } from '@/api/AuthContext';

function safeParseDate(value) {
  if (!value) return null;
  const d = typeof value === 'string' ? parseISO(value) : new Date(value);
  return isValid(d) ? d : null;
}


export default function ShippingDashboard() {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('versandbereit');
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const { data: orders = [], isLoading } = useQuery({
    queryKey: ['orders'],
    queryFn: () => base44.entities.Order.list(),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  // Abfahrt (Versandbereit → Unterwegs): sevkiyat aracı yola çıkıyor
  const abfahrtMutation = useMutation({
    mutationFn: async (order) => {
      const now = new Date().toISOString();
      return base44.entities.Order.update(order.id, {
        status: 'Unterwegs',
        shipped_at: now,
        shipped_by: user?.email || null,
        updated_date: now,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['orders']);
      toast.success('Lieferung gestartet — Status: Unterwegs');
    },
    onError: (err) => toast.error('Fehler: ' + err.message),
  });

  // Lieferung bestätigen (Unterwegs → Geliefert): müşteri imzası geri geldi
  const liefernMutation = useMutation({
    mutationFn: async (order) => {
      const now = new Date().toISOString();
      return base44.entities.Order.update(order.id, {
        status: 'Geliefert',
        delivered_at: now,
        delivered_by: user?.email || null,
        updated_date: now,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['orders']);
      toast.success('Lieferung bestätigt — Status: Geliefert');
    },
    onError: (err) => toast.error('Fehler: ' + err.message),
  });

  const matchesSearch = (order) => {
    const needle = searchTerm.toLowerCase();
    return (
      (order.customer_name || order.lead_name || '').toLowerCase().includes(needle) ||
      (order.order_number || '').toLowerCase().includes(needle) ||
      (order.delivery_note_number || '').toLowerCase().includes(needle)
    );
  };

  const versandbereitOrders = orders
    .filter(o => o.status === 'Versandbereit' && matchesSearch(o))
    .sort((a, b) => {
      const da = safeParseDate(a.delivery_note_at || a.updated_date);
      const db = safeParseDate(b.delivery_note_at || b.updated_date);
      return (db?.getTime() || 0) - (da?.getTime() || 0);
    });

  const unterwegsOrders = orders
    .filter(o => o.status === 'Unterwegs' && matchesSearch(o))
    .sort((a, b) => {
      const da = safeParseDate(a.shipped_at || a.updated_date);
      const db = safeParseDate(b.shipped_at || b.updated_date);
      return (db?.getTime() || 0) - (da?.getTime() || 0);
    });

  const geliefertOrders = orders.filter(o => o.status === 'Geliefert');

  const todayStr = new Date().toISOString().split('T')[0];
  const shippedToday = [...unterwegsOrders, ...geliefertOrders].filter((o) => {
    const upd = o.shipped_at || o.updated_date;
    return upd && upd.startsWith(todayStr);
  });

  const tabOrders = activeTab === 'versandbereit' ? versandbereitOrders : unterwegsOrders;

  // Ortalama teslim süresi: son 30 geliefert siparişin shipped→delivered ort.
  const recentDelivered = geliefertOrders.slice(0, 30);
  const avgHours = (() => {
    const diffs = recentDelivered
      .map((o) => {
        const start = safeParseDate(o.shipped_at || o.delivery_note_at || o.created_date);
        const end = safeParseDate(o.delivered_at || o.updated_date);
        if (!start || !end) return null;
        const h = differenceInHours(end, start);
        return h >= 0 ? h : null;
      })
      .filter((n) => n !== null);
    if (diffs.length === 0) return null;
    return diffs.reduce((s, x) => s + x, 0) / diffs.length;
  })();

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-24">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Truck className="w-6 h-6 text-slate-700" />
          Versandverwaltung
        </h1>
      </div>

      <div className="grid md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium text-slate-500 uppercase">Versandbereit</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-indigo-600">{versandbereitOrders.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium text-slate-500 uppercase">Unterwegs</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">{unterwegsOrders.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium text-slate-500 uppercase">Heute Aktiv</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-600">{shippedToday.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium text-slate-500 uppercase">Ø Lieferzeit</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {avgHours === null ? '—' : `${avgHours.toFixed(1)} Std`}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Phase Tabs */}
      <div className="flex gap-2 border-b border-slate-200 mb-4">
        <button
          onClick={() => setActiveTab('versandbereit')}
          className={cn(
            'px-4 py-2 text-sm font-semibold border-b-2 -mb-[1px] transition-colors',
            activeTab === 'versandbereit'
              ? 'border-indigo-600 text-indigo-700'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          )}
        >
          Versandbereit ({versandbereitOrders.length})
          <span className="ml-2 text-[10px] font-normal text-slate-400">→ Abfahrt</span>
        </button>
        <button
          onClick={() => setActiveTab('unterwegs')}
          className={cn(
            'px-4 py-2 text-sm font-semibold border-b-2 -mb-[1px] transition-colors',
            activeTab === 'unterwegs'
              ? 'border-amber-600 text-amber-700'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          )}
        >
          Unterwegs ({unterwegsOrders.length})
          <span className="ml-2 text-[10px] font-normal text-slate-400">→ Lieferung bestätigen</span>
        </button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <CardTitle>Versandaufträge</CardTitle>
            <div className="relative w-full md:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Suchen (Kunde, Bestell-Nr)..."
                className="pl-9"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-slate-500">
                  <th className="text-left py-3 font-medium">Bestell-Nr</th>
                  <th className="text-left py-3 font-medium">Kunde</th>
                  <th className="text-left py-3 font-medium">Datum</th>
                  <th className="text-center py-3 font-medium">Status</th>
                  <th className="text-right py-3 font-medium">Aktionen</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {isLoading ? (
                  <tr>
                    <td colSpan="5" className="text-center py-10 text-slate-400">
                      Lädt...
                    </td>
                  </tr>
                ) : tabOrders.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center py-10 text-slate-400">
                      Keine Aufträge in diesem Status.
                    </td>
                  </tr>
                ) : (
                  tabOrders.map((order) => {
                    const created = safeParseDate(order.created_at || order.created_date);
                    return (
                      <tr key={order.id} className="hover:bg-slate-50/50 transition-colors">
                        <td className="py-4 font-medium text-slate-900">
                          #{order.order_number || String(order.id || '').slice(0, 8)}
                          {order.delivery_note_number && (
                            <div className="text-[10px] font-mono text-indigo-600">
                              {order.delivery_note_number}
                            </div>
                          )}
                        </td>
                        <td className="py-4">
                          <div className="font-medium text-slate-900">
                            {order.customer_name || order.lead_name || 'Unbekannter Kunde'}
                          </div>
                          <div className="text-xs text-slate-500">
                            {getOrderItems(order).length} Positionen
                          </div>
                        </td>
                        <td className="py-4 text-slate-500">
                          {created ? format(created, 'dd.MM.yyyy HH:mm', { locale: de }) : '-'}
                        </td>
                        <td className="py-4 text-center">
                          <StatusBadge status={order.status || 'Neu'} type="orderStatus" />
                        </td>
                        <td className="py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <OrderDocuments order={order} />
                            {order.status === 'Versandbereit' && (
                              <Button
                                size="sm"
                                disabled={abfahrtMutation.isPending}
                                className="h-8 gap-1 bg-amber-600 hover:bg-amber-700"
                                onClick={() => abfahrtMutation.mutate(order)}
                              >
                                {abfahrtMutation.isPending
                                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  : <Truck className="w-3.5 h-3.5" />}
                                <span className="hidden sm:inline">Abfahrt</span>
                              </Button>
                            )}
                            {order.status === 'Unterwegs' && (
                              <Button
                                size="sm"
                                disabled={liefernMutation.isPending}
                                className="h-8 gap-1 bg-emerald-600 hover:bg-emerald-700"
                                onClick={() => liefernMutation.mutate(order)}
                              >
                                {liefernMutation.isPending
                                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  : <CheckCircle2 className="w-3.5 h-3.5" />}
                                <span className="hidden sm:inline">Geliefert</span>
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Lieferschein üretimi artık Components/orders/LieferscheinPDF.jsx
          üzerinden yapılır; off-screen template burada gereksiz. */}
    </div>
  );
}
